import { useEffect, useMemo } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME_DUTCH, searchFeaturedListings, searchRecentListings, featuredListingsSelector } from './LandingPage.duck';
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from 'react-intl';
import { NamedRedirect } from '../../components';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    onSearchFeaturedListings,
    onSearchRecentListings,
    featuredListings,
  } = props;
  const config = useConfiguration();
  const intl = useIntl();
  
  useEffect(() => {
    if (!featuredListings || featuredListings.length === 0) {
      onSearchFeaturedListings(config);
      onSearchRecentListings(config);
    }
  }, [config, featuredListings, onSearchFeaturedListings, onSearchRecentListings]);

  const isDutch = (intl?.locale || '').toLowerCase().startsWith('nl');
  if (!isDutch) {
    return <NamedRedirect name="EnglishLandingPage" />;
  }

  // Memoize options to prevent creating new object reference on every render
  const options = useMemo(() => ({
    featuredListings: featuredListings,
  }), [featuredListings]);

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[(ASSET_NAME_DUTCH)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      options={options}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};

  const featuredListings = featuredListingsSelector(state);

  return { pageAssetsData, inProgress, error, featuredListings };
};

const mapDispatchToProps = dispatch => ({
  onSearchFeaturedListings: config => dispatch(searchFeaturedListings(config)),
  onSearchRecentListings: config => dispatch(searchRecentListings(config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
